import React, { useState, useEffect, useRef } from 'react';
import Quill from 'quill';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import ModalSection from '../ModalSection';
import PromptModel from '../../../../models/PromptModel';
import { SIDEBAR_ACTIONS } from '../../../../../constants/actionsConstants';
import RightModalLayout from '../../../../layouts/Modal/RightModalLayout';
import QuillComponent from '../QuillComponent';
import ConversationFlowToolsSelector from './ConversationFlowToolsSelector';
import {
  editorHtmlToStoredPrompt,
  nextFlowToolIndex,
  tokensToEditorHtml
} from './conversationFlowTools';
import {addToDataLayer} from '../../../../../util/gtm';

const StepByStepModal = ({ agentId }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('ChatbotIA');

  const [stepByStep, setStepByStep] = useState('');
  const [promptId, setPromptId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const quillRef = useRef(null);
  const savedRangeRef = useRef(null);

  useEffect(() => {
    loadStepByStepContent();
  }, [agentId]);

  const toggleStepByStepModal = () => {
    dispatch({ type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR });
  };

  const loadStepByStepContent = () => {
    if (!agentId) {
      showtoast(t('config.error_loading'));
      setLoading(false);
      return;
    }

    setLoading(true);
    PromptModel.get(agentId, { type: 'conversation_flow' })
      .then((response) => {
        const prompts = response.data || [];
        const flowPrompt = prompts.find(p => p.type === 'conversation_flow');

        if (flowPrompt) {
          setStepByStep(tokensToEditorHtml(flowPrompt.prompt || ''));
          setPromptId(flowPrompt.id);
        } else {
          setStepByStep('');
          setPromptId(null);
        }
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        showtoast(err.error || t('config.error_loading'));
      });
  };

  const updateInfo = (value) => {
    setStepByStep(value);
  };

  const rememberCursor = () => {
    const editor = quillRef.current?.getEditor?.();
    savedRangeRef.current = editor?.getSelection() ?? null;
  };

  const insertTool = (tool) => {
    const editor = quillRef.current?.getEditor?.();
    const source = editor?.root?.innerHTML || stepByStep || '';
    const toolIndex = nextFlowToolIndex(source);

    if (!editor) {
      setStepByStep((current) => tokensToEditorHtml(`${current || ''}{{${tool.id}:${toolIndex}}}`));
      return;
    }

    const range = savedRangeRef.current || editor.getSelection();
    const index = range ? range.index : Math.max(editor.getLength() - 1, 0);
    editor.insertEmbed(index, 'toolPill', { id: tool.id, index: String(toolIndex), label: tool.label }, 'user');
    editor.insertText(index + 1, '\u00A0', 'user');
    editor.setSelection(index + 2, 0, 'silent');
    savedRangeRef.current = null;
  };

  useEffect(() => {
    if (loading) return undefined;

    const editor = quillRef.current?.getEditor?.();
    if (!editor) return undefined;

    const onClick = (event) => {
      const removeButton = event.target.closest?.('[data-remove-tool-index]');
      if (!removeButton || !editor.root.contains(removeButton)) return;

      const pill = removeButton.closest('.mia-tool-pill');
      const blot = Quill.find(pill);
      if (!blot) return;

      event.preventDefault();
      event.stopPropagation();
      const index = editor.getIndex(blot);
      editor.deleteText(index, 1, 'user');
    };

    editor.root.addEventListener('click', onClick);
    return () => editor.root.removeEventListener('click', onClick);
  }, [loading]);

  const onSubmit = () => {
    if (!agentId) {
      showtoast(t('config.error_loading'));
      return;
    }

    const prompt = editorHtmlToStoredPrompt(stepByStep);
    const params = promptId
      ? { prompt }
      : { type: 'conversation_flow', prompt };

    setSaving(true);

    const savePromise = promptId
      ? PromptModel.update(agentId, promptId, params)
      : PromptModel.save(agentId, params);

    savePromise
      .then((result) => {
        if (!promptId && result?.id) {
          setPromptId(result.id);
        }
        addToDataLayer({ event: 'mia_config_flow' });
        setTimeout(() => {
          showtoast(t('config.save_success'));
          setSaving(false);
          toggleStepByStepModal();
        }, 1000);
      })
      .catch((error) => {
        setSaving(false);
        showtoast(error.message || t('config.save_error'));
      });
  };

  return (
    <div className="tw-wrapper">
      <RightModalLayout
        title={t('config.conversation_flow.modal_title')}
        submitAction={onSubmit}
        submitLabel={t('guides.sidebar_create.update')}
        submitDisabled={saving}
      >
        <ModalSection
          title={t('config.conversation_flow.section_1_title')}
          description={t('config.conversation_flow.section_1_description')}
          afterContent={(
            <>
              <div
                onMouseDown={(event) => {
                  event.preventDefault();
                  rememberCursor();
                }}
              >
                <ConversationFlowToolsSelector
                  agentId={agentId}
                  onSelectTool={insertTool}
                />
              </div>
              <QuillComponent
                editorRef={quillRef}
                name="step_by_step"
                value={stepByStep || ''}
                onChange={(value) => { updateInfo(value); }}
                overflowHidden={false}
                style={{ height: 'calc(100vh - 420px)' }}
              />
            </>
          )}
        />
      </RightModalLayout>
    </div>
  );
};

export default StepByStepModal;
